import { Code, Mail } from 'lucide-react';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Github,
  LinkButton,
  PageErrorState,
  PageHeader,
  PageState,
  Reveal,
  ShimmerLoader,
} from '@/shared/ui';
import { ProjectCard } from './components/project-card';
import {
  useProfile,
  useProjects,
  useSiteContent,
} from '@personal-portfolio/queries';
import { toErrorMessage } from '@/shared/lib/query-error';
import { useLocation } from 'react-router';
import { Helmet } from '@dr.pogodin/react-helmet';
import type { Project } from '@personal-portfolio/contract';

export function ProjectsPage() {
  const {
    data: rawProjectsData,
    isPending: projectsLoading,
    isFetching: projectsFetching,
    isPaused: projectsPaused,
    error: projectsError,
    refetch: refetchProjects,
  } = useProjects();
  const { data: profileData } = useProfile();
  const error = toErrorMessage(projectsError, projectsPaused);

  const location = useLocation();
  const content = useSiteContent();
  const page = content.pages.projects;
  const header = { title: page.title, subtitle: page.subtitle };

  const projectsData: Project[] = rawProjectsData ?? [];

  const githubLink = profileData?.socialLinks?.find(
    (link) => link.type === 'github',
  );

  const handleRetry = () => {
    if (projectsFetching) return;
    void refetchProjects();
  };

  const renderLoading = () => (
    <>
      <PageHeader {...header} />
      <div className="mb-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-xl border border-border p-6">
            <ShimmerLoader lines={2} height="h-6" className="mb-4" />
            <ShimmerLoader lines={3} height="h-4" className="mb-4" />
            <ShimmerLoader lines={1} height="h-4" className="w-2/3" />
          </div>
        ))}
      </div>
    </>
  );

  const renderError = () => (
    <>
      <PageHeader {...header} />
      <PageErrorState
        onRetry={handleRetry}
        icon={<Code />}
        message={error ?? undefined}
      />
    </>
  );

  // Content render prop (the empty-data decision lives here)
  const renderContent = () => {
    if (projectsData.length === 0) {
      return (
        <>
          <PageHeader {...header} />
          <Empty className="min-h-[400px]">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Code />
              </EmptyMedia>
              <EmptyTitle>{page.empty.title}</EmptyTitle>
              <EmptyDescription>{page.empty.message}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              {githubLink?.url && (
                <LinkButton
                  href={githubLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {page.githubCta}
                  <Github className="ml-1" />
                </LinkButton>
              )}
            </EmptyContent>
          </Empty>
        </>
      );
    }

    return (
      <>
        <PageHeader {...header} />
        <div className="mb-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {projectsData.map((project, index) => (
            <Reveal
              key={project.slug}
              // Stagger within the two-column row, not down the whole list.
              index={index % 2}
              className="h-full"
            >
              <ProjectCard
                id={project.slug}
                title={project.title}
                description={project.description}
                links={project.links}
                isFeatured={project.isFeatured}
                isPublic={project.isPublic}
                type={project.type}
                status={project.status}
              />
            </Reveal>
          ))}
        </div>

        <section className="rounded-3xl border border-border bg-muted p-6 text-center sm:p-12">
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-4 text-2xl leading-snug font-semibold tracking-tight text-foreground sm:text-3xl">
              {page.collaboration.heading}
            </h2>
            <p className="mb-8 text-lg leading-relaxed text-muted-foreground">
              {page.collaboration.body}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {profileData?.email && (
                <LinkButton href={`mailto:${profileData.email}`}>
                  <Mail className="ml-1" />
                  {page.collaboration.contactCta}
                </LinkButton>
              )}
              {githubLink?.url && (
                <LinkButton
                  variant="outline"
                  href={githubLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Github className="ml-1" />
                  {page.collaboration.githubCta}
                </LinkButton>
              )}
            </div>
          </div>
        </section>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{page.metaTitle}</title>
        <link
          rel="canonical"
          href={`${content.site.url}${location.pathname}`}
        />
      </Helmet>
      <PageState
        loading={projectsLoading}
        error={error}
        hasData={!!rawProjectsData}
        renderLoading={renderLoading}
        renderError={renderError}
        renderContent={renderContent}
      />
    </>
  );
}
